import {
    type MenuItem,
    PLANNING_NAV_ITEMS,
    RELATIONS_NAV_ITEMS,
    VENDORS_NAV_ITEMS,
    STYLE_NAV_ITEMS,
    LOGISTICS_NAV_ITEMS,
    FINANCE_NAV_ITEMS,
    DAY_MANAGEMENT_NAV_ITEMS,
} from '../constants/sidebarNavGroups';
import {
    EVENTS_ENABLED,
    COMMUNICATIONS_ENABLED,
    GUESTS_ENABLED,
    VENDORS_ENABLED,
    PROCUREMENT_ENABLED,
    VENDOR_BOOKINGS_ENABLED,
    CATALOGUE_ENABLED,
    ASOEBI_ENABLED,
    GIFT_REGISTRY_ENABLED,
    MENU_ENABLED,
    MUSIC_ENABLED,
    STATIONERY_ENABLED,
    DECOR_ENABLED,
    VENUES_ENABLED,
    LOGISTICS_ENABLED,
    BUDGET_ENABLED,
    QUOTES_ENABLED,
    PAYMENTS_ENABLED,
    EVENT_DAY_ENABLED,
    GUIDELINES_ENABLED,
    TEAM_ENABLED,
} from '../constants/featureFlags';

/** Dashboard and Settings are the way back in: they can never be hidden. */
const PINNED_NAV_IDS: ReadonlySet<string> = new Set(['dashboard', 'settings']);

export const isNavPinned = (id: string): boolean => PINNED_NAV_IDS.has(id);

/** The hidden-entry ids as stored in `settings.menu.hidden`, cleaned: strings only, no repeats, no pinned entries. */
export function sanitizeHiddenMenu(raw: unknown): string[] {
    if (!Array.isArray(raw)) return [];
    const seen = new Set<string>();
    for (const id of raw) {
        if (typeof id === 'string' && id !== '' && !isNavPinned(id)) seen.add(id);
    }
    return Array.from(seen);
}

export function isNavHidden(id: string, hidden: readonly string[]): boolean {
    return !isNavPinned(id) && hidden.includes(id);
}

/** A new list with `id` hidden or shown; the list passed in is never changed. */
export function setNavHidden(hidden: readonly string[], id: string, hide: boolean): string[] {
    const without = sanitizeHiddenMenu(hidden).filter(x => x !== id);
    return hide && !isNavPinned(id) ? [...without, id] : without;
}

export interface MenuSection {
    title: string;
    items: MenuItem[];
}

/** The sections the sidebar draws, in order. One list for the sidebar and for the Settings > Menu card. */
export function menuSections(): MenuSection[] {
    return [
        { title: 'Planning', items: PLANNING_NAV_ITEMS },
        { title: 'Relations', items: RELATIONS_NAV_ITEMS },
        { title: 'Vendors', items: VENDORS_NAV_ITEMS },
        { title: 'Style', items: STYLE_NAV_ITEMS },
        { title: 'Logistics', items: LOGISTICS_NAV_ITEMS },
        { title: 'Finance', items: FINANCE_NAV_ITEMS },
        { title: 'Day Management', items: DAY_MANAGEMENT_NAV_ITEMS },
    ];
}

// Which top-level item id each feature flag gates. Items absent from Ans's
// screens entirely are already left out of constants/sidebarNavGroups.ts —
// this map only toggles items that DO have a flag. Built fresh on every
// call (NOT a module-level constant) so a flag change is read live rather
// than frozen at the value it held when Sidebar.tsx first loaded — this
// matters for tests that mutate a mocked flag between renders.
export function isNavFlagEnabled(id: string): boolean {
    const itemFlag: Record<string, boolean> = {
        events: EVENTS_ENABLED,
        communications: COMMUNICATIONS_ENABLED,
        guests: GUESTS_ENABLED,
        team: TEAM_ENABLED,
        vendors: VENDORS_ENABLED,
        'vendor-bookings': VENDOR_BOOKINGS_ENABLED,
        procurement: PROCUREMENT_ENABLED,
        catalogue: CATALOGUE_ENABLED,
        // Bundles predates ASOEBI_ENABLED and is not gated by it, so the
        // 'asoebi' parent itself is never gated; only its Fabrics/Orders children are.
        'asoebi-fabrics': ASOEBI_ENABLED,
        'asoebi-orders': ASOEBI_ENABLED,
        'gift-registry': GIFT_REGISTRY_ENABLED,
        menu: MENU_ENABLED,
        music: MUSIC_ENABLED,
        stationery: STATIONERY_ENABLED,
        decor: DECOR_ENABLED,
        venue: VENUES_ENABLED,
        logistics: LOGISTICS_ENABLED,
        budget: BUDGET_ENABLED,
        'budget-variances': BUDGET_ENABLED,
        quotes: QUOTES_ENABLED,
        payments: PAYMENTS_ENABLED,
        'event-day': EVENT_DAY_ENABLED,
        guidelines: GUIDELINES_ENABLED,
    };
    return itemFlag[id] !== false;
}
